// Lightweight i18n for the fleet UI, mirroring the commandBus i18n approach.

export type Locale = 'fr' | 'en'

let currentLocale: Locale = 'fr'

export function getLocale(): Locale {
  return currentLocale
}

export function setLocale(locale: Locale): void {
  currentLocale = locale
}

export interface FleetUiStrings {
  title: string
  createFleet: string
  fleetName: string
  members: string
  profile: string
  version: string
  save: string
  saving: string
  cancel: string
  delete: string
  addLuminaires: string
  removeFromFleet: string
  noFleets: string
  noMembers: string
  searchLuminaire: string
  alreadyAssigned: string
  assignProfile: string
  editProfile: string
  // minigrid link
  minigrids: string
  addMinigrid: string
  noMinigrids: string
  removeMinigrid: string
  searchMinigrid: string
  notSyncedNoMinigrid: string
  noMinigridWarning: string
  // divergence
  divergence: string
  momi: string
  cloudVersion: string
  momiVersion: string
  status: string
  inSync: string
  diverged: string
  lastSync: string
  overwrite: string
  download: string
  pushing: string
  pulling: string
  pushResults: string
  pushed: string
  failed: string
  // push sans aucune cible (flotte sans minigrid lié → 0 momi joignable)
  noPushTarget: string
  refresh: string
  // delete confirmation
  deleteConfirmTitle: string
  deleteConfirmMessage: (name: string) => string
  // validation
  noDaysSelected: string
  // errors
  conflictError: string
  genericError: string
  // control priorities panel
  controlPriorities: string
  cpColPriority: string
  cpColSource: string
  cpColCondition: string
  cpColEffect: string
  cpColStatus: string
  cpBadgeActive: string
  cpBadgeInDev: string
  cpManualName: string
  cpManualCondition: string
  cpManualEffect: string
  cpLdrName: string
  cpLdrCondition: string
  cpLdrEffect: string
  cpCalendarName: string
  cpCalendarCondition: string
  cpCalendarEffect: string
  cpDigName: string
  cpDigCondition: string
  cpDigEffect: string
  cpProfileName: string
  cpProfileCondition: string
  cpProfileEffect: string
  cpDefaultName: string
  cpDefaultCondition: string
  cpDefaultEffect: string
  cpFooter: string
  cpClampNote: string
}

const STRINGS: Record<Locale, FleetUiStrings> = {
  fr: {
    title: 'Parcs de luminaires',
    createFleet: 'Créer un parc',
    fleetName: 'Nom du parc',
    members: 'Membres',
    profile: 'Profil',
    version: 'Version',
    save: 'Enregistrer',
    saving: 'Enregistrement…',
    cancel: 'Annuler',
    delete: 'Supprimer',
    addLuminaires: 'Ajouter des luminaires',
    removeFromFleet: 'Retirer',
    noFleets: 'Aucun parc. Créez-en un pour commencer.',
    noMembers: 'Aucun luminaire dans ce parc.',
    searchLuminaire: 'Rechercher un luminaire…',
    alreadyAssigned: 'Déjà dans un parc',
    assignProfile: 'Définir le profil',
    editProfile: 'Modifier le profil',
    minigrids: 'Minigrids',
    addMinigrid: 'Ajouter une minigrid',
    noMinigrids: 'Aucune minigrid liée',
    removeMinigrid: 'Retirer',
    searchMinigrid: 'Rechercher une minigrid…',
    notSyncedNoMinigrid: '⚠ Non synchronisée — aucune minigrid',
    noMinigridWarning:
      '⚠ Cette flotte n’est liée à aucune minigrid : elle ne sera pas synchronisée. Ajoutez une minigrid ci-dessous.',
    divergence: 'Divergence de version',
    momi: 'MoMi',
    cloudVersion: 'Version cloud',
    momiVersion: 'Version MoMi',
    status: 'État',
    inSync: 'Synchronisé',
    diverged: 'Divergent',
    lastSync: 'Dernière synchro',
    overwrite: 'Écraser (push)',
    download: 'Télécharger (pull)',
    pushing: 'Envoi…',
    pulling: 'Récupération…',
    pushResults: 'Résultats du push',
    pushed: 'Envoyé',
    failed: 'Échec',
    noPushTarget:
      'Aucune cible : la flotte n’est liée à aucune minigrid, la configuration n’a atteint aucune lampe.',
    refresh: 'Rafraîchir',
    deleteConfirmTitle: 'Supprimer le parc',
    deleteConfirmMessage: (name: string) =>
      `Voulez-vous vraiment supprimer le parc « ${name} » ? Cette action est irréversible.`,
    noDaysSelected: 'Sélectionnez au moins un jour.',
    conflictError:
      'Conflit : version concurrente ou luminaire déjà dans un autre parc.',
    genericError: 'Une erreur est survenue.',
    controlPriorities: 'Priorités de contrôle',
    cpColPriority: 'Priorité',
    cpColSource: 'Source',
    cpColCondition: 'Condition d’activation',
    cpColEffect: 'Effet',
    cpColStatus: 'Statut',
    cpBadgeActive: 'Actif (éditable)',
    cpBadgeInDev: 'En développement',
    cpManualName: 'Manuel',
    cpManualCondition: 'Downlink MANUAL DIM valide reçu',
    cpManualEffect: 'Niveau imposé par la commande manuelle',
    cpLdrName: 'LDR',
    cpLdrCondition: 'Activé & signal du capteur de luminosité haut',
    cpLdrEffect: '0 % (éteint en pleine lumière)',
    cpCalendarName: 'Calendrier',
    cpCalendarCondition: 'Activé & jour (position solaire)',
    cpCalendarEffect: '0 % le jour ; sinon le profil prend le relais la nuit',
    cpDigName: 'DIG',
    cpDigCondition: 'Activé & entrée DIG haute',
    cpDigEffect: 'Niveau défini par la configuration DIG',
    cpProfileName: 'Profil',
    cpProfileCondition: 'Activé & palier de gradation actif (heure)',
    cpProfileEffect: 'Niveau du profil 0x21',
    cpDefaultName: 'Gradation par défaut',
    cpDefaultCondition: 'Aucune autre source active',
    cpDefaultEffect: 'Niveau par défaut',
    cpFooter:
      'Le luminaire applique la source active la mieux classée (DALI / 0–10V).',
    cpClampNote:
      'Le Calendrier borne (clamp) le Profil quand « clamps_profiles » est activé.',
  },
  en: {
    title: 'Luminaire fleets',
    createFleet: 'Create fleet',
    fleetName: 'Fleet name',
    members: 'Members',
    profile: 'Profile',
    version: 'Version',
    save: 'Save',
    saving: 'Saving…',
    cancel: 'Cancel',
    delete: 'Delete',
    addLuminaires: 'Add luminaires',
    removeFromFleet: 'Remove',
    noFleets: 'No fleets. Create one to get started.',
    noMembers: 'No luminaires in this fleet.',
    searchLuminaire: 'Search a luminaire…',
    alreadyAssigned: 'Already in a fleet',
    assignProfile: 'Set profile',
    editProfile: 'Edit profile',
    minigrids: 'Minigrids',
    addMinigrid: 'Add a minigrid',
    noMinigrids: 'No minigrid linked',
    removeMinigrid: 'Remove',
    searchMinigrid: 'Search a minigrid…',
    notSyncedNoMinigrid: '⚠ Not synchronized — no minigrid',
    noMinigridWarning:
      '⚠ This fleet is not linked to any minigrid: it will not be synchronized. Add a minigrid below.',
    divergence: 'Version divergence',
    momi: 'MoMi',
    cloudVersion: 'Cloud version',
    momiVersion: 'MoMi version',
    status: 'Status',
    inSync: 'In sync',
    diverged: 'Diverged',
    lastSync: 'Last sync',
    overwrite: 'Overwrite (push)',
    download: 'Download (pull)',
    pushing: 'Pushing…',
    pulling: 'Pulling…',
    pushResults: 'Push results',
    pushed: 'Pushed',
    failed: 'Failed',
    noPushTarget:
      'No target: the fleet is not linked to any minigrid, the configuration reached no lamp.',
    refresh: 'Refresh',
    deleteConfirmTitle: 'Delete fleet',
    deleteConfirmMessage: (name: string) =>
      `Delete fleet "${name}"? This cannot be undone.`,
    noDaysSelected: 'Select at least one day.',
    conflictError:
      'Conflict: concurrent version, or luminaire already in another fleet.',
    genericError: 'Something went wrong.',
    controlPriorities: 'Control priorities',
    cpColPriority: 'Priority',
    cpColSource: 'Source',
    cpColCondition: 'Activation condition',
    cpColEffect: 'Effect',
    cpColStatus: 'Status',
    cpBadgeActive: 'Active (editable)',
    cpBadgeInDev: 'In development',
    cpManualName: 'Manual',
    cpManualCondition: 'Valid MANUAL DIM downlink received',
    cpManualEffect: 'Level forced by the manual command',
    cpLdrName: 'LDR',
    cpLdrCondition: 'Enabled & light sensor signal high',
    cpLdrEffect: '0% (off in full daylight)',
    cpCalendarName: 'Calendar',
    cpCalendarCondition: 'Enabled & daytime (solar position)',
    cpCalendarEffect: '0% during the day; otherwise the profile takes over at night',
    cpDigName: 'DIG',
    cpDigCondition: 'Enabled & DIG input high',
    cpDigEffect: 'Level set by the DIG configuration',
    cpProfileName: 'Profile',
    cpProfileCondition: 'Enabled & dimming step active (time)',
    cpProfileEffect: 'Level from the 0x21 profile',
    cpDefaultName: 'Default dim',
    cpDefaultCondition: 'No other source active',
    cpDefaultEffect: 'Default level',
    cpFooter:
      'The luminaire applies the highest-ranked active source (DALI / 0–10V).',
    cpClampNote:
      'Calendar clamps the Profile when "clamps_profiles" is enabled.',
  },
}

export function fleetStrings(locale: Locale): FleetUiStrings {
  return STRINGS[locale] ?? STRINGS.fr
}
